import { useEffect, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useKorektyData from '@/hooks/useKorektyData'
import { useDateRangeFilter } from './filters/DateRangeFilter'
import { useStatusFilter } from './filters/StatusFilter'
import { useCompanyFilter } from './filters/CompanyFilter'

import { useKorektaSelection } from './hooks/useKorektaSelection'
import { useKorektaActions } from './hooks/useKorektaActions'

import SearchActionsBar from '@/widgets/SearchActionsBar'
import KorektyTable from './components/KorektyTable'
import AssignPlacowkaModal from '../common/AssignPlacowkaModal'
import LoadMoreSection from '@/widgets/LoadMoreSection'

import SettingsService from '@/services/SettingsService'
import { handleError } from '@/lib/dialogHelper'

export default function Invoices() {
	const { t } = useTranslation()
	const [systemSettings, setSystemSettings] = useState(null)
	
	const {
		data: invoicesData,
		loading,
		loadingMore,
		error,
		totalCount,
		hasMore,
		loadData: loadInvoicesData,
		handleLoadMore,
		setError,
		setSorting: setInvoicesSorting,
		handleSearch,
		sortColumn,
		sortDirection,
		searchQuery,
		updateFilters,
	} = useKorektyData()
	
	const { selectedInvoices, handleSelectAll, handleSelectInvoice, clearSelection, getSelectionState } = useKorektaSelection()
	const {
		isDeleting,
		handleDeleteSelected,
		assignInvoiceId,
		isAssigning,
		openAssignPlacowka,
		closeAssignPlacowka,
		handleAssignPlacowka,
	} = useKorektaActions(loadInvoicesData)
	const { selectedStatus, handleStatusFilter, getStatusDisplay } = useStatusFilter(updateFilters)
	const { fromDate, toDate, handleDateRangeFilter, getDateRangeDisplay } = useDateRangeFilter(updateFilters)

	const companies = useMemo(() => {
		if (!Array.isArray(systemSettings?.companies)) return []
		return systemSettings.companies
			.filter(company => company?.id)
			.map(company => ({
				id: company.id,
				name: company.companyName || company.id,
			}))
			.sort((a, b) => a.name.localeCompare(b.name))
	}, [systemSettings])

	const { selectedCompanyId, handleCompanyFilter, getCompanyDisplay } = useCompanyFilter(updateFilters, companies)

	useEffect(() => {
		if (!error) return
		handleError(error, setError)
	}, [error, setError])

	useEffect(() => {
		loadInvoicesData()
	}, [loadInvoicesData])

	useEffect(() => {
		SettingsService.getSystem().then(({ data }) => {
			if (data) setSystemSettings(data)
		})
	}, [])

	const handleSort = useCallback(
		column => {
			const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
			setInvoicesSorting(column, newDirection)
		},
		[sortColumn, sortDirection, setInvoicesSorting]
	)

	const handleSearchChange = useCallback(
		e => {
			handleSearch(e.target.value)
		},
		[handleSearch]
	)

	const onDeleteSelected = useCallback(() => {
		handleDeleteSelected(selectedInvoices, clearSelection)
	}, [handleDeleteSelected, selectedInvoices, clearSelection])

	const onSelectAll = useCallback(
		checked => {
			handleSelectAll(checked, invoicesData)
		},
		[handleSelectAll, invoicesData]
	)

	const { isAllSelected, isIndeterminate } = useMemo(
		() => getSelectionState(invoicesData),
		[getSelectionState, invoicesData]
	)

	const buttons = [
		{ type: 'delete', label: t('korekty.list.actions.delete'), count: selectedInvoices.size, onClick: onDeleteSelected, loading: isDeleting },
		{
			type: 'date',
			label: t('korekty.list.actions.status'),
			active: !!selectedStatus,
			onClick: handleStatusFilter,
			title: selectedStatus
				? t('korekty.list.statusFilter.activeTitle', { status: getStatusDisplay() })
				: t('korekty.list.statusFilter.buttonTitle'),
		},
		{
			type: 'date',
			label: t('korekty.list.actions.company'),
			active: !!selectedCompanyId,
			onClick: handleCompanyFilter,
			title: selectedCompanyId
				? t('korekty.list.companyFilter.activeTitle', { company: getCompanyDisplay() })
				: t('korekty.list.companyFilter.buttonTitle'),
		},
		{
			type: 'date',
			label: t('korekty.list.actions.dateRange'),
			active: !!(fromDate || toDate),
			onClick: handleDateRangeFilter,
			title: getDateRangeDisplay() || t('korekty.list.dateFilter.buttonTitle'),
		},
	]

	return (
		<>
			<SearchActionsBar
				value={searchQuery}
				onChange={handleSearchChange}
				placeholder={t('korekty.list.searchPlaceholder')}
				buttons={buttons}
			/>

			<KorektyTable
				invoicesData={invoicesData}
				loading={loading}
				selectedInvoices={selectedInvoices}
				onSelectAll={onSelectAll}
				onSelectInvoice={handleSelectInvoice}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				sortColumn={sortColumn}
				sortDirection={sortDirection}
				systemSettings={systemSettings}
				onSort={handleSort}
				onAssignPlacowka={openAssignPlacowka}
			/>

			<LoadMoreSection
				hasMore={hasMore}
				loadingMore={loadingMore}
				onLoadMore={handleLoadMore}
				dataLength={invoicesData.length}
				totalCount={totalCount}
			/>

			<AssignPlacowkaModal
				isOpen={!!assignInvoiceId}
				onClose={closeAssignPlacowka}
				onSelect={handleAssignPlacowka}
				isAssigning={isAssigning}
			/>
		</>
	)
}
